import { LatLng } from "types/map";

/** Rough regional centers used when geolocation is unavailable. */
const TIMEZONE_CENTERS: Record<string, LatLng> = {
  // Europe
  "Europe/Paris": { lat: 46.603354, lng: 1.888334 },
  "Europe/London": { lat: 54.5, lng: -2.5 },
  "Europe/Berlin": { lat: 51.16, lng: 10.45 },
  "Europe/Madrid": { lat: 40.2, lng: -3.7 },
  "Europe/Rome": { lat: 42.5, lng: 12.5 },
  "Europe/Oslo": { lat: 64.5, lng: 11.5 },
  "Europe/Stockholm": { lat: 62.2, lng: 15.5 },
  "Europe/Amsterdam": { lat: 52.2, lng: 5.3 },
  "Europe/Brussels": { lat: 50.5, lng: 4.5 },
  "Europe/Zurich": { lat: 46.8, lng: 8.2 },
  "Europe/Vienna": { lat: 47.6, lng: 14.1 },
  "Europe/Warsaw": { lat: 52.1, lng: 19.4 },
  "Europe/Lisbon": { lat: 39.4, lng: -8.2 },
  "Europe/Dublin": { lat: 53.4, lng: -8.0 },
  "Europe/Athens": { lat: 39.1, lng: 22.9 },
  "Europe/Helsinki": { lat: 64.0, lng: 26.0 },
  "Europe/Copenhagen": { lat: 56.0, lng: 10.0 },
  "Europe/Prague": { lat: 49.8, lng: 15.5 },
  "Europe/Bucharest": { lat: 45.9, lng: 25.0 },
  "Europe/Moscow": { lat: 61.5, lng: 105.0 },
  // Americas
  "America/New_York": { lat: 39.0, lng: -77.0 },
  "America/Chicago": { lat: 39.8, lng: -89.6 },
  "America/Denver": { lat: 39.7, lng: -104.9 },
  "America/Los_Angeles": { lat: 36.8, lng: -119.4 },
  "America/Toronto": { lat: 50.0, lng: -85.0 },
  "America/Vancouver": { lat: 53.7, lng: -127.6 },
  "America/Mexico_City": { lat: 23.6, lng: -102.5 },
  "America/Sao_Paulo": { lat: -14.2, lng: -51.9 },
  "America/Buenos_Aires": { lat: -38.4, lng: -63.6 },
  // Asia / Pacific / Africa / Oceania
  "Asia/Tokyo": { lat: 36.2, lng: 138.3 },
  "Asia/Shanghai": { lat: 35.9, lng: 104.2 },
  "Asia/Hong_Kong": { lat: 22.3, lng: 114.2 },
  "Asia/Singapore": { lat: 1.35, lng: 103.8 },
  "Asia/Dubai": { lat: 25.2, lng: 55.3 },
  "Asia/Kolkata": { lat: 22.0, lng: 79.0 },
  "Asia/Seoul": { lat: 36.5, lng: 127.9 },
  "Australia/Sydney": { lat: -25.3, lng: 133.8 },
  "Australia/Melbourne": { lat: -25.3, lng: 133.8 },
  "Pacific/Auckland": { lat: -40.9, lng: 174.9 },
  "Africa/Johannesburg": { lat: -28.5, lng: 24.7 },
  "Africa/Cairo": { lat: 26.8, lng: 30.8 },
  "Africa/Lagos": { lat: 9.1, lng: 8.7 },
  "Atlantic/Azores": { lat: 38.5, lng: -28.0 },
  "Africa/Casablanca": { lat: 31.8, lng: -7.1 },
};

const REGION_CENTERS: Array<{ prefix: string; center: LatLng }> = [
  { prefix: "Europe/", center: { lat: 50.0, lng: 10.0 } },
  { prefix: "America/", center: { lat: 37.0, lng: -95.0 } },
  { prefix: "Asia/", center: { lat: 34.0, lng: 100.0 } },
  { prefix: "Australia/", center: { lat: -25.3, lng: 133.8 } },
  { prefix: "Pacific/", center: { lat: -8.0, lng: 160.0 } },
  { prefix: "Africa/", center: { lat: 5.0, lng: 20.0 } },
  { prefix: "Atlantic/", center: { lat: 35.0, lng: -30.0 } },
  { prefix: "Indian/", center: { lat: -10.0, lng: 70.0 } },
];

const WORLD_FALLBACK: LatLng = { lat: 20, lng: 0 };

export const TIMEZONE_DEFAULT_ZOOM = 7;
export const GPS_DEFAULT_ZOOM = 13;

export const getBrowserTimezone = (): string =>
  Intl.DateTimeFormat().resolvedOptions().timeZone || "";

/** Map the browser IANA timezone to a rough map center. */
export const getTimezoneFallbackCenter = (
  timeZone: string = getBrowserTimezone()
): LatLng => {
  if (timeZone && TIMEZONE_CENTERS[timeZone]) {
    return TIMEZONE_CENTERS[timeZone];
  }

  const region = REGION_CENTERS.find(({ prefix }) =>
    timeZone.startsWith(prefix)
  );
  return region?.center ?? WORLD_FALLBACK;
};
